import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TuiTitle } from '@taiga-ui/core';
import {
  IPerformanceLimitsValue,
  PerformanceLimitsTableComponent,
} from '@shared/components/performance-limits-table/performance-limits-table';

export interface IPerformanceLimitsDatapoint {
  key: string;
  datapointId: string;
  name: string;
  unitLabel: string;
  value: IPerformanceLimitsValue;
}

export interface IPerformanceLimitsDatapointChange {
  key: string;
  value: IPerformanceLimitsValue;
}

export interface IPerformanceLimitsDatapointInvalidChange {
  key: string;
  invalid: boolean;
}

/**
 * Contents of a single metric's limits card — title + one `app-performance-limits-table`
 * per datapoint. The card chrome (`tuiCardMedium`) is the caller's concern, not this
 * component's.
 */
@Component({
  selector: 'app-performance-limits-metric',
  templateUrl: 'performance-limits-metric.html',
  styleUrl: 'performance-limits-metric.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TuiTitle, PerformanceLimitsTableComponent],
})
export class PerformanceLimitsMetricComponent {
  readonly metricName = input.required<string>();
  readonly datapoints = input.required<readonly IPerformanceLimitsDatapoint[]>();
  readonly editable = input(true);
  /** `null` (default) renders every datapoint; a list renders only matching `datapointId`s. */
  readonly visibleDatapointIds = input<readonly string[] | null>(null);

  readonly valueChange = output<IPerformanceLimitsDatapointChange>();
  readonly invalidChange = output<IPerformanceLimitsDatapointInvalidChange>();

  protected readonly visibleDatapoints = computed(() => {
    const ids = this.visibleDatapointIds();
    const all = this.datapoints();
    return ids === null ? all : all.filter((datapoint) => ids.includes(datapoint.datapointId));
  });

  protected onValueChange(key: string, value: IPerformanceLimitsValue): void {
    this.valueChange.emit({ key, value });
  }

  protected onInvalidChange(key: string, invalid: boolean): void {
    this.invalidChange.emit({ key, invalid });
  }
}
